import React, { useEffect, useState } from 'react';
import { XIcon, SaveIcon, GlobeIcon, TagIcon, BellIcon, CheckIcon, PlusIcon, MinusIcon } from 'lucide-react';
import { Button } from '../ui/Button';
interface EditAlertModalProps {
  alert: any;
  onClose: () => void;
  onSave: (data: any) => void;
}
export function EditAlertModal({
  alert,
  onClose,
  onSave
}: EditAlertModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    keywords: [''],
    countries: [],
    frequency: 'Instantanée'
  });
  // Initialize form with alert data
  useEffect(() => {
    if (alert) {
      setFormData({
        name: alert.name,
        keywords: [...alert.keywords],
        countries: [...alert.countries],
        frequency: alert.frequency
      });
    }
  }, [alert]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleKeywordChange = (index: number, value: string) => {
    const updatedKeywords = [...formData.keywords];
    updatedKeywords[index] = value;
    setFormData({
      ...formData,
      keywords: updatedKeywords
    });
  };
  const addKeyword = () => {
    setFormData({
      ...formData,
      keywords: [...formData.keywords, '']
    });
  };
  const removeKeyword = (index: number) => {
    if (formData.keywords.length > 1) {
      const updatedKeywords = formData.keywords.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        keywords: updatedKeywords
      });
    }
  };
  const toggleCountry = (country: string) => {
    const updatedCountries = formData.countries.includes(country) ? formData.countries.filter(c => c !== country) : [...formData.countries, country];
    setFormData({
      ...formData,
      countries: updatedCountries
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Filter out empty keywords
    const cleanedData = {
      ...formData,
      keywords: formData.keywords.filter(k => k.trim() !== '')
    };
    onSave({
      ...alert,
      ...cleanedData
    });
  };
  // Available countries
  const availableCountries = ['Maroc', 'Algérie', 'Tunisie', 'Sénégal', "Côte d'Ivoire", 'Cameroun', 'Ghana', 'Nigeria', 'Kenya', 'Afrique du Sud'];
  return <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50 p-4" data-id="element-123">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-124">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-125">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-126">
            Modifier l'alerte
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-127">
            <XIcon size={20} data-id="element-128" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]" data-id="element-129">
          <form onSubmit={handleSubmit} data-id="element-130">
            <div className="space-y-6" data-id="element-131">
              <div data-id="element-132">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-133">
                  Nom de l'alerte
                </label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" placeholder="Ex: Projets d'infrastructure au Maroc" required data-id="element-134" />
              </div>
              <div data-id="element-135">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-136">
                  Mots-clés
                </label>
                <div className="space-y-2" data-id="element-137">
                  {formData.keywords.map((keyword, index) => <div key={index} className="flex items-center space-x-2" data-id="element-138">
                      <input type="text" value={keyword} onChange={e => handleKeywordChange(index, e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" placeholder="Ex: construction, infrastructure, routes" data-id="element-139" />
                      {formData.keywords.length > 1 && <button type="button" onClick={() => removeKeyword(index)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full" data-id="element-140">
                          <MinusIcon size={18} data-id="element-141" />
                        </button>}
                    </div>)}
                  <button type="button" onClick={addKeyword} className="flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-142">
                    <PlusIcon size={16} className="mr-1" data-id="element-143" />
                    Ajouter un mot-clé
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500" data-id="element-144">
                  Ajoutez des mots-clés pertinents pour votre recherche
                </p>
              </div>
              <div data-id="element-145">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-146">
                  Pays
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-147">
                  {availableCountries.map(country => <div key={country} className="flex items-center" data-id="element-148">
                      <input type="checkbox" id={`country-${country}`} checked={formData.countries.includes(country)} onChange={() => toggleCountry(country)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-149" />
                      <label htmlFor={`country-${country}`} className="ml-2 block text-sm text-gray-700" data-id="element-150">
                        {country}
                      </label>
                    </div>)}
                </div>
                {formData.countries.length === 0 && <p className="mt-1 text-sm text-red-500" data-id="element-151">
                    Veuillez sélectionner au moins un pays
                  </p>}
              </div>
              <div data-id="element-152">
                <label htmlFor="frequency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-153">
                  Fréquence de notification
                </label>
                <select id="frequency" name="frequency" value={formData.frequency} onChange={handleInputChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" data-id="element-154">
                  <option value="Instantanée" data-id="element-155">Instantanée</option>
                  <option value="Quotidienne" data-id="element-156">Quotidienne</option>
                  <option value="Hebdomadaire" data-id="element-157">Hebdomadaire</option>
                </select>
              </div>
            </div>
            <div className="mt-8 flex justify-end space-x-3" data-id="element-158">
              <Button variant="outline" type="button" onClick={onClose} className="px-4" data-id="element-159">
                Annuler
              </Button>
              <Button variant="primary" type="submit" className="flex items-center shadow-md shadow-yellow-500/20" disabled={formData.countries.length === 0} data-id="element-160">
                <SaveIcon size={16} className="mr-2" data-id="element-161" />
                Enregistrer les modifications
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}